import { gql, useMutation, useQuery } from "@apollo/client";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "@mui/material";
import { isMCloudEmbedded } from "@src/utils";
import { useFlags } from "launchdarkly-react-client-sdk";
import mixpanel from "mixpanel-browser";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useNavigate } from "react-router-dom";

import { useNoteUsage, usePremiumFeature } from "../../hooks";
import { APPEND_RECORDING } from "./edit/api/mutations";
import { EditableNote } from "./edit/EditableNote";
import { submitTestingSampleAudio } from "../../api/UploadAudio";
import { recordedNoteSteps } from "../../assets/data/JoyrideTourSteps";
import { isTestEnv, testUsers } from "../../constants.js";
import { NOTE_FIELDS } from "../../graphql/fragments/note";
import { RETRY_FAILED_AUTO_SOAP } from "../../graphql/mutations/RetryFailedAutoSoap";
import withTemplatesMessageModal from "../../hocs/withTemplatesMessageModal.js";
import { alert } from "../common/Alert";
import { Banner } from "../common/Banner";
import JoyrideTour from "../common/modals/JoyrideTour";
import UpgradeModal from "../common/premium/UpgradeModal";
import {
  testRecordingsData,
  TestRecordTypeDropdown,
  SubmitTestRecordingButton,
} from "../dashboard/TestRecordings";
import PageTitle from "../layout/PageTitle.js";

export const GET_NOTE = gql`
  ${NOTE_FIELDS}
  query getNote($uuid: String) {
    note(uuid: $uuid) {
      user {
        uuid
        shouldUseAutoSoaps
        firstName
        lastName
        account {
          headerInfoPdf
          logoFileLink
        }
      }
      patient {
        uuid
        name
        lastNameOfOwner
        sex
        species
      }
      autoTemplates {
        uuid
        name
      }
      ...NoteFields
      attachments {
        uuid
        fileName
        fileLink
        contentType
        createdAt
      }
      keyHistory
      keyMedications
      vaccinationStatus
      audioStorageLink
      isAutoSoap
      isIncomplete
      isUnacceptable
      dataExtraction
      thumbsUp
      feedback
      timeToProcess
      isFree
    }
  }
`;

const CHECKOUT_NOTE = gql`
  mutation checkoutNote($uuid: String) {
    checkoutNote(noteUuid: $uuid) {
      ok
      user {
        uuid
        firstName
        lastName
      }
    }
  }
`;

const CHECKIN_NOTE = gql`
  mutation checkinNote($uuid: String) {
    checkinNote(noteUuid: $uuid) {
      ok
    }
  }
`;

const TestFunctionality = ({ note }) => {
  const [selectedTestRecordType, setSelectedTestRecordType] =
    useState("medical_record");

  const [appendRecording] = useMutation(APPEND_RECORDING);

  if (!note?.user || !note?.uuid) {
    return null;
  }
  const createNewNote = async (
    audioStorageLink,
    storageObjectName,
  ) => {
    return await appendRecording({
      variables: {
        noteUuid: note.uuid,
        storageObjectName: storageObjectName,
        createdAt: moment().format(),
        lastUpdatedAt: moment().format(),
      },
    })
      .then(() => {
        alert("success", "Recording has been appended!");
        setTimeout(function () {
          window.location.reload();
        }, 1000);
      })
      .catch((e) => {
        alert("error", e.message);
      });
  };

  const appendRecordingToNote = () => {
    let recording = testRecordingsData.find(
      (recording) => recording.value == selectedTestRecordType,
    );

    const { audioUrl } = recording;

    submitTestingSampleAudio({
      createNewNote,
      audioUrl,
    });
  };

  if (isMCloudEmbedded()) {
    return null;
  }

  if (!(isTestEnv || testUsers.includes(note.user?.email))) {
    return null;
  }
  return (
    <div className="flex flex-row">
      <div className="flex flex-col ">
        <TestRecordTypeDropdown
          selectedTestRecordType={selectedTestRecordType}
          setSelectedTestRecordType={setSelectedTestRecordType}
          testRecordings={testRecordingsData}
        />
      </div>
      <div className="flex flex-col ">
        <SubmitTestRecordingButton
          onClick={appendRecordingToNote}
          text={"Append Selected"}
        />
      </div>
    </div>
  );
};

function EditNote() {
  const [runJoyrideTour, setRunJoyrideTour] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [checkoutNote, { data: checkoutData }] =
    useMutation(CHECKOUT_NOTE);
  const [checkinNote] = useMutation(CHECKIN_NOTE);

  const { freeMode } = useFlags();
  const { hasFeatureAccess, UserPermissions } = usePremiumFeature();
  useEffect(() => {
    checkoutNote({
      variables: {
        uuid: noteUUID,
      },
    });
    return () => {
      checkinNote({
        variables: {
          uuid: noteUUID,
        },
      });
    };
  }, []);

  const [retryFailedAutoSoap] = useMutation(RETRY_FAILED_AUTO_SOAP);

  const navigate = useNavigate();
  const { hasReachedAutoSoapLimit } = useNoteUsage();
  const isNotFreePlanAndHasReachedAutoSoapLimit =
    !freeMode && hasReachedAutoSoapLimit;

  const { noteUUID } = useParams();

  const {
    data,
    loading,
    refetch: refetchNote,
  } = useQuery(GET_NOTE, {
    variables: { uuid: noteUUID },
    fetchPolicy: "network-only",
  });

  if (!data || loading) {
    return null;
  }

  function renderBanner() {
    if (data?.note?.isAutoSoap && data?.note?.isIncomplete) {
      return (
        <Banner bgStyle="bg-yellow-400">
          We've detected that the audio for this note might be
          incomplete. Please check the audio recording. <br /> If you
          think this is a mistake,please send the note to our support
          team using the "Something Wrong?" button.
        </Banner>
      );
    } else if (data?.note?.isUnacceptable) {
      return (
        <Banner bgStyle="bg-red-400">
          We've detected that this audio cannot be made into an note.{" "}
          Please check the audio recording. <br /> If you think this
          is a mistake, please send the note to our support team using
          the "Something Wrong?" button.
        </Banner>
      );
    } else {
      return null;
    }
  }

  const handleRetryFailedAutoSoap = async () => {
    try {
      await retryFailedAutoSoap({
        variables: {
          noteUuid: noteUUID,
          jobType: data?.note?.jobType,
          templateUuid: data?.note?.autoTemplates?.uuid,
        },
      });
      alert("warning", "Submitting as Automatic Note...");
      mixpanel.track("Note reprocessed", {
        noteUuid: noteUUID,
        jobType: data?.note?.jobType,
        templateUuid: data?.note?.autoTemplates?.uuid,
        source: "Note failed banner",
      });
      navigate("/dashboard");
    } catch (e) {
      mixpanel.track("Failed to reprocess note", {
        noteUuid: noteUUID,
        jobType: data?.note?.jobType,
        templateUuid: data?.note?.autoTemplates?.uuid,
        source: "Note failed banner",
        error: e.message,
      });
      alert("error", e.message);
    }
  };

  const renderPageTitleChildren = () => {
    const isFreePlanNote = data?.note?.isFree;
    if (isFreePlanNote) {
      return (
        <Tooltip
          title="This note was generated using our standard AI model. If you have a pro plan, reprocess this note to use our advanced AI models for higher quality SOAP notes."
          arrow
        >
          <h5
            className={`flex items-center text-sm justify-center py-1 px-2
             rounded-full bg-[#A1A1AA] text-white dark:text-white ${
               !hasFeatureAccess(
                 UserPermissions.PREMIUM_DEFAULT_NOTES_ENABLED,
               )
                 ? "cursor-pointer"
                 : "cursor-default"
             }`}
            onClick={() => {
              if (
                !hasFeatureAccess(
                  UserPermissions.PREMIUM_DEFAULT_NOTES_ENABLED,
                )
              ) {
                setShowUpgradeModal(true);
                mixpanel.track("Clicked Standard Quality Badge");
              }
            }}
            data-testid="standard-quality-pill"
          >
            Standard Quality
            <FontAwesomeIcon
              icon={faInfoCircle}
              className="ml-1.5 text-sm"
            />
          </h5>
        </Tooltip>
      );
    }
    return null;
  };
  return (
    <>
      <UpgradeModal
        shouldShow={showUpgradeModal}
        setShouldShow={setShowUpgradeModal}
        featureName="Advanced AI"
      />
      <TestFunctionality note={data?.note} />
      <PageTitle
        title="Edit Note"
        onClick={() => {
          setRunJoyrideTour(true);
        }}
        showHelpButton
        alignChildren="left"
      >
        {freeMode && renderPageTitleChildren()}
      </PageTitle>

      <JoyrideTour
        steps={recordedNoteSteps}
        runJoyrideTour={runJoyrideTour}
        setRunJoyrideTour={setRunJoyrideTour}
      />

      {renderBanner()}

      {!data?.note?.isTagged &&
        !data?.note?.isApproved &&
        !data?.note?.isAutoSoap &&
        data?.note?.audioStorageLink &&
        !!isNotFreePlanAndHasReachedAutoSoapLimit &&
        !!data?.note?.user?.shouldUseAutoSoaps && (
          <div>
            <Banner bgStyle="bg-green-400">
              You can turn this recording into an Auto-Note!{" "}
              <button
                className="underline"
                onClick={handleRetryFailedAutoSoap}
              >
                Click here to turn this recording into a note
                automatically.
              </button>
            </Banner>
          </div>
        )}
      <EditableNote
        note={data?.note}
        status={checkoutData?.checkoutNote}
        refetchNote={refetchNote}
      />
    </>
  );
}

export default withTemplatesMessageModal(EditNote);
